import type {
  ConnectionDto,
  DiagramSummaryConnectionDto,
  DiagramSummaryNodeDto,
  NodeDto,
} from '../../generated/contracts';
import { NodeKind } from '../../net/wire';

/**
 * The one adapter between the viewer's bounded wire shape and the shape the existing renderers
 * already speak.
 *
 * **Why an adapter rather than reusing `NodeDto`/`ConnectionDto` on the wire.** The summary is
 * deliberately narrower than the room snapshot — no version, no lock metadata, no timestamps, no
 * org or diagram ids, no strokes (plan/diagram-as-the-product.md's "bounded summary fetch", and
 * `DiagramSummaryDto`'s own remarks). Widening it back out to the full DTO purely to reuse
 * `NodeMeshFactory`/`ConnectionRenderer` would have thrown that away for the convenience of two
 * functions. Widening it here instead keeps both halves honest: the wire stays bounded, and the
 * renderers stay the ones the room already uses rather than a second implementation of the same
 * drawing that could drift from it.
 *
 * The fields invented below are exactly the ones a renderer never reads for drawing: an id it
 * needs for naming, a kind it needs for the metadata it stamps, and zeros for everything whose
 * only purpose is reconciliation the viewer does not do.
 */

/** Placeholder ids for the fields a `NodeDto` declares and drawing never reads. All-zero rather
 *  than a real id, so anything that ever *did* start reading them fails loudly instead of
 *  silently attributing a viewed diagram to the wrong org. */
const NOT_APPLICABLE_ID = '00000000-0000-0000-0000-000000000000';

/** Epoch, for the same reason: `NodeDto.updatedAt` exists for reconciliation, and a viewer
 *  reconciles nothing. */
const NOT_APPLICABLE_TIMESTAMP = '1970-01-01T00:00:00.000Z';

export function toDrawableNode(node: DiagramSummaryNodeDto): NodeDto {
  return {
    id: node.id,
    orgId: NOT_APPLICABLE_ID,
    diagramId: NOT_APPLICABLE_ID,
    kind: NodeKind.Shape,
    shapeType: node.shapeType,
    text: node.text,
    style: { color: node.color },
    transform: {
      // Canonical, unfitted — the viewer has no room to fit to (SPEC-131, and
      // `DiagramViewport`'s own doc comment).
      position: node.position,
      rotation: node.rotation,
      scale: node.scale,
    },
    version: 0,
    updatedAt: NOT_APPLICABLE_TIMESTAMP,
  };
}

export function toDrawableConnection(connection: DiagramSummaryConnectionDto): ConnectionDto {
  return {
    id: connection.id,
    orgId: NOT_APPLICABLE_ID,
    diagramId: NOT_APPLICABLE_ID,
    fromNodeId: connection.fromNodeId,
    toNodeId: connection.toNodeId,
    label: connection.label,
    style: {
      color: connection.color,
      arrow: connection.arrow,
      line: connection.line,
    },
  };
}
